/**
 * Cuánto dura un evento, en horas.
 *
 * La fecha de fin dejó de pedirse a mano: se elige una duración y el fin sale
 * de sumarla al inicio. El motivo es que nadie monta una activación pensando
 * «termina el jueves a las 21:00» — la piensa como «son cuatro horas», y pedir
 * la fecha de fin obligaba a hacer esa cuenta mentalmente y a repetirla cada
 * vez que se movía el inicio.
 */
export const DURACIONES = [3, 4, 6, 8] as const;
export type Duracion = (typeof DURACIONES)[number];

/** La que se propone si nadie dice otra cosa. */
export const DURACION_POR_DEFECTO: Duracion = 3;

/**
 * `YYYY-MM-DDTHH:mm` con N horas sumadas, en el protocolo local del DatePicker.
 *
 * Se pasa por `Date` en vez de sumar al texto de la hora porque cruzar la
 * medianoche tiene que avanzar el día: un evento que empieza a las 22:00 y dura
 * tres horas termina a la 01:00 **del día siguiente**, no a las 25:00 del mismo.
 */
export function sumarHoras(local: string, horas: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(local);
  if (!m) return local;
  const [, y, mo, d, hh, mm] = m;
  const fin = new Date(Number(y), Number(mo) - 1, Number(d), Number(hh) + horas, Number(mm));
  const p = (n: number) => String(n).padStart(2, '0');
  return `${fin.getFullYear()}-${p(fin.getMonth() + 1)}-${p(fin.getDate())}T${p(fin.getHours())}:${p(fin.getMinutes())}`;
}

/**
 * Las horas que separan a dos marcas locales, redondeadas.
 *
 * Hace falta al EDITAR: el evento guardado trae dos fechas, no una duración, y
 * hay que traducirlas a la opción del desplegable. Lo que no case con ninguna
 * —los eventos de varios días que ya existen— se deja como está hasta que
 * alguien toque el desplegable; no se recorta a la fuerza, porque eso
 * cambiaría un evento en marcha por el mero hecho de abrir su ficha.
 */
export function horasEntre(inicio: string, fin: string): number | null {
  const a = Date.parse(inicio);
  const b = Date.parse(fin);
  if (Number.isNaN(a) || Number.isNaN(b) || b <= a) return null;
  return Math.round((b - a) / 3600_000);
}

/** La opción del desplegable que corresponde a un rango guardado, si alguna. */
export function duracionDe(inicio: string, fin: string): Duracion | null {
  const h = horasEntre(inicio, fin);
  return h != null && (DURACIONES as readonly number[]).includes(h) ? (h as Duracion) : null;
}
